import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { isEmptyString } from '@common/utils';
import { ImageUploadIcon } from 'public/assets';
import { useAppDispatch } from '@store/hooks';
import { alertMessage } from '@slices/alertSlice';
import Button from '@components/commons/button/Button';
import OnboardingInput from '@components/commons/input/OnboardingInput';
import {
  CustomProfile,
  MyCharacterInfo,
  OnboardingPopup,
  OtherCharacterInfo,
  PlayStory,
} from '@customTypes/chapter';

const MAX_NAME_SIZE = 10;

export const TextPopup = ({
  isLastPopup,
  description,
  onNext,
}: {
  isLastPopup: boolean;
  description: string;
  onNext: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div css={popupContainer}>
      <div className="text">{description}</div>
      <Button
        title={
          isLastPopup
            ? t('common_screen_button_start')
            : t('common_screen_label_next')
        }
        onClick={onNext}
        customCSS={buttonStyle}
      />
    </div>
  );
};

export const NamePopup = ({
  isLastPopup,
  playStory,
  onBoarding,
  onName,
  choiceProfiles,
  onUpdateProfileImage,
  currentName,
}: {
  isLastPopup: boolean;
  playStory: PlayStory;
  onBoarding: OnboardingPopup;
  onName: (
    otherCharacterInfo?: OtherCharacterInfo & { popupId: number },
    myCharacterInfo?: MyCharacterInfo & { popupId: number },
  ) => void;
  choiceProfiles: CustomProfile[];
  onUpdateProfileImage: (currentName?: {
    firstName: string;
    lastName?: string;
  }) => void;
  currentName?: {
    firstName: string;
    lastName?: string;
  };
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [firstName, setFirstName] = useState<string>(
    currentName?.firstName || playStory?.defaultFirstName,
  );
  const [lastName, setLastName] = useState<string | undefined>(
    currentName?.lastName || playStory?.defaultLastName,
  );
  const [profileImage, setProfileImage] = useState<string | undefined>(
    undefined,
  );
  const [customImage, setCustomImage] = useState<CustomProfile>();

  useEffect(() => {
    const mainProfile = choiceProfiles?.find((item) => item.isMainCharacter);
    const characterProfile = choiceProfiles?.find(
      (item) => item.characterId === onBoarding?.character?.chrId,
    );

    const customImage = choiceProfiles.find(
      (item) => item.popupId === onBoarding.popupId,
    );
    setCustomImage(customImage);

    setProfileImage(undefined);
    setFirstName(
      currentName?.firstName ||
        (onBoarding.type === 'Name' ? playStory?.defaultFirstName : ''),
    );
    setLastName(
      currentName?.lastName ||
        (onBoarding.type === 'Name'
          ? currentName?.lastName || playStory?.defaultLastName
          : ''),
    );
    customImage
      ? setProfileImage(
          customImage.profileImageFile?.link || customImage?.fileUrl,
        )
      : setProfileImage(
          onBoarding.type === 'Name'
            ? mainProfile?.profileImageFile?.link ||
                playStory?.mainCharacterImageFile?.link
            : characterProfile?.profileImageFile?.link ||
                onBoarding.character?.imageFile?.link,
        );
  }, [onBoarding, playStory, choiceProfiles, currentName]);

  const onChangeName = () => {
    if (!onBoarding.onlyName && !isEmptyString(playStory?.defaultLastName)) {
      const nameLength = lastName?.length ?? 0;
      if (nameLength < 1 || nameLength > MAX_NAME_SIZE) {
        dispatch(
          alertMessage({
            message: t('chat_popup_description_last_name_must_be_over_1', {
              value: MAX_NAME_SIZE,
            }),
            type: 'warning',
          }),
        );
        return;
      }
    }
    if (firstName.length < 1 || firstName.length > MAX_NAME_SIZE) {
      dispatch(
        alertMessage({
          message: t('chat_popup_description_first_name_must_be_over_1', {
            value: MAX_NAME_SIZE,
          }),
          type: 'warning',
        }),
      );
      return;
    }

    const mainProfile = choiceProfiles?.find((item) => item.isMainCharacter);
    const characterProfile = choiceProfiles?.find(
      (item) => item.characterId === onBoarding?.character?.chrId,
    );

    const myInfo: (MyCharacterInfo & { popupId: number }) | undefined =
      onBoarding.type === 'Name'
        ? {
            firstName: firstName,
            lastName: lastName,
            profileImageId:
              customImage?.profileImageFileId ||
              mainProfile?.profileImageFileId ||
              playStory?.mainCharacterImageFile?.fileId,
            popupId: onBoarding.popupId,
          }
        : undefined;

    const otherInfo: (OtherCharacterInfo & { popupId: number }) | undefined =
      onBoarding.type === 'Name'
        ? undefined
        : {
            originName: onBoarding.character?.name ?? '',
            customFirstName: firstName,
            customLastName: lastName,
            profileImageFileId:
              customImage?.profileImageFileId ||
              characterProfile?.profileImageFileId ||
              onBoarding?.character?.imageFileId,
            popupId: onBoarding.popupId,
          };
    onName(otherInfo, myInfo);
  };

  return (
    <div css={popupContainer}>
      <div className="title">{onBoarding.description}</div>

      <div className="profile-container">
        {profileImage ? (
          <Image
            src={profileImage}
            alt="profile"
            layout="fill"
            objectFit="cover"
            className="profile"
            unoptimized={true}
          />
        ) : (
          <div className="default-profile" />
        )}
        {(onBoarding.canUploadProfileImage ||
          onBoarding.popupProfileImages?.length > 1) && (
          <div
            className="upload"
            onClick={() => {
              onUpdateProfileImage({
                firstName,
                lastName,
              });
            }}
          >
            <ImageUploadIcon />
          </div>
        )}
      </div>

      <div css={input}>
        {!onBoarding.onlyName && (
          <OnboardingInput
            width={'30%'}
            value={lastName || ''}
            placeholder={t('chat_popup_title_last_name')}
            isFixMainChrName={
              playStory.isFixMainChrName && onBoarding.type === 'Name'
            }
            onChangeText={(text) => setLastName(text)}
          />
        )}
        <OnboardingInput
          width={!onBoarding.onlyName ? '70%' : '100%'}
          placeholder={t('chat_popup_title_first_name')}
          value={firstName || ''}
          isFixMainChrName={
            playStory.isFixMainChrName && onBoarding.type === 'Name'
          }
          onChangeText={(text) => setFirstName(text)}
        />
      </div>
      <div className="hint-container">
        {playStory.isFixMainChrName && onBoarding.type === 'Name' ? (
          <div className="hint">
            {t('chat_popup_description_dont_change_name')}
          </div>
        ) : (
          <div className="hint">{t('chat_popup_description_change_name')}</div>
        )}
      </div>
      <Button
        title={
          isLastPopup
            ? t('common_screen_button_start')
            : t('common_screen_label_next')
        }
        isDisable={
          onBoarding.onlyName
            ? isEmptyString(firstName)
            : isEmptyString(firstName) || isEmptyString(lastName)
        }
        onClick={onChangeName}
        customCSS={buttonStyle}
      />
    </div>
  );
};

const popupContainer = css`
  width: 100%;
  padding: 0 1rem 1rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  .title {
    min-width: 90%;
    ${theme.fontV3.body.body01Bold};
    text-align: center;
    color: var(--blackA85, rgba(0, 0, 0, 0.85));
    margin-bottom: 1.25rem;
  }

  .text {
    padding: 0 0.25rem;
    min-width: 90%;
    ${theme.fontV3.body.body03Reg};
    text-align: center;
    color: var(--blackA75, rgba(0, 0, 0, 0.75));
    margin-bottom: 1.5rem;
  }

  .hint-container {
    margin: 1.25em 0;
    line-height: 1.25em;
  }

  .hint {
    color: var(--blackA25, rgba(0, 0, 0, 0.25));
    ${theme.fontV3.body.body05Reg};
    min-width: 90%;
    text-align: center;
    line-height: 1.25em;
  }

  .profile-container {
    position: relative;
    width: 8rem;
    height: 8rem;
    border-radius: 50%;
    border: 1px solid var(--blackA10, rgba(0, 0, 0, 0.1));
    margin-bottom: 0.75em;

    .profile {
      border-radius: 50%;
    }

    .default-profile {
      width: 100%;
      height: 100%;
      background-color: ${theme.colors.gray400};
      border-radius: 50%;
    }

    .upload {
      cursor: pointer;
      position: absolute;
      bottom: -3%;
      right: 0;
    }
  }

  @media screen and (max-width: ${theme.devices.mobile}) {
    width: 100%;
  }
`;

const input = css`
  display: flex;
  justify-content: center;
`;

const buttonStyle = css`
  width: 100%;
  height: 3.25rem;
  cursor: pointer;
  ${theme.fontV3.body.body03Bold};
`;
